import { useCallback, useEffect, useState } from "react";
import { useUser } from "./useUser";
import { initialState } from "@uiw/react-json-view";

/**
 * Scope determines how the storage key is prefixed:
 * - 'user': Prefixed with user.id (per-user state)
 * - 'device': No prefix (shared across all users on this device/browser)
 */
export type SessionStateScope = 'user' | 'device';

const resolveInitialValue = <T extends any | (() => T)>(initial: T): T => {
  if (typeof initial === "function") {
    return initial();
  }

  return initial;
}

/**
 * `useSessionState` — per-user persisted state.
 *
 * STORAGE: **localStorage**, despite the name. It was sessionStorage once and
 * moved; the name stayed because call sites and stored keys were everywhere (see
 * `useToggleableSessionState` below, which is the one that genuinely uses
 * sessionStorage). So this value outlives the tab AND the browser session — it is
 * still there tomorrow.
 *
 * Keys are prefixed with `${user.id}:` under the default `'user'` scope, so two
 * accounts on one device do not share state. Pass `'device'` to opt out of the
 * per-user prefix.
 *
 * @param sessionKey     Key WITHOUT the user prefix; the scope adds it.
 * @param initialValue   Used when nothing is stored. May be a lazy factory.
 * @param scope          `'user'` (default, prefixed) or `'device'` (shared).
 * @returns              `[state, setState, clear]` — `clear` also resets state.
 */
export const useSessionState = <T>(
  sessionKey: string, 
  initialValue: T | (() => T), 
  scope: SessionStateScope = 'user'
) => {
  const [user] = useUser();
  const key = scope === 'user' && user ? `${user.id}:${sessionKey}` : sessionKey;
  const [state, setState] = useState<T>(() => {
    if ( typeof window === "undefined" ) {
      return resolveInitialValue(initialValue);
    }

    const storedValue = localStorage.getItem(key);
    try {
      return storedValue ? JSON.parse(storedValue) : resolveInitialValue(initialValue);
    } catch(e) {
      return resolveInitialValue(initialValue);
    }
  });

  useEffect(() => {
    const storedValue = localStorage.getItem(key);
    if ( storedValue === 'undefined' || typeof storedValue === 'undefined' ) {
      localStorage.removeItem(key);
      return;
    }

    try {
      if ( storedValue ) {
        setState(JSON.parse(storedValue));
      } else {
        setState(resolveInitialValue(initialValue));
      }
    } catch(e) {
      console.error("Error parsing stored value: ", e);
      console.log({storedValue}, "STORED VALUE");
    }
  }, [key]);

  useEffect(() => {
    if (scope === 'user' && !user) return;
    localStorage.setItem(key, JSON.stringify(state));
  }, [scope, user, key, state]);

  const clear = useCallback(() => {
    localStorage.removeItem(key);
    setState(resolveInitialValue(initialValue));
  }, [key, initialValue]);

  return [state, setState, clear] as const;
}

/**
 * Allows storing a value in session when the toggle is on, and in memory when the toggle is off.
 *
 * NOTE: this is the **sessionStorage** hook, unlike its sibling `useSessionState`
 * above (which uses localStorage). The `persistent` flag is what selects it, so
 * passing `false` keeps the value in memory only — that is how `Collapsible`
 * supports opting out of remembering an open/closed state.
 *
 * @param persistent When true, the value is stored in session storage. When false, the value is stored in memory.
 * @param key The key to use for storing the value.
 * @param initialValue The initial value to use when the value is not found in storage.
 */
export const useToggleableSessionState = <T>(persistent: boolean, key: string, initialValue: T) => {
  const [state, setState] = useState<T>(() => {
    if ( persistent && typeof window !== "undefined" ) {
      const storedValue = sessionStorage.getItem(key);
      try {
        return storedValue ? JSON.parse(storedValue) : initialValue;
      } catch(e) {
        return initialValue;
      }
    } else {
      return initialValue;
    }
  });

  useEffect(() => {
    if ( persistent && typeof window !== "undefined" ) {
      const storedValue = sessionStorage.getItem(key);
      try {
        if ( storedValue ) {
          setState(JSON.parse(storedValue));
        } else {
          setState(initialValue);
        }
      } catch(e) {
        console.error("Error parsing stored value: ", e);
      }
    }
  }, [key]);

  useEffect(() => {
    if ( persistent && typeof window !== "undefined" ) {
      sessionStorage.setItem(key, JSON.stringify(state));
    }
  }, [state]);

  return [state, setState] as const;
}
